import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useCalculos } from "@/hooks/useCalculos";
import { CalculoRevisionalResult } from "@/types/calculos";
import { addToHistory, useCalculoPrefill } from "@/hooks/useCalculoHistory";


export const CalculosRevisionais = () => {
  const [valorFinanciado, setValorFinanciado] = useState("");
  const [taxaContratual, setTaxaContratual] = useState("");
  const [taxaLegal, setTaxaLegal] = useState("");
  const [numParcelas, setNumParcelas] = useState("");
  const [resultado, setResultado] = useState<CalculoRevisionalResult | null>(null);
  
  const { calcularRevisional, loading } = useCalculos();

  useCalculoPrefill("revisionais", (v) => {
    setValorFinanciado(v.valorFinanciado ?? "");
    setTaxaContratual(v.taxaContratual ?? "");
    setTaxaLegal(v.taxaLegal ?? "");
    setNumParcelas(v.numParcelas ?? "");
    setResultado(null);
  });

  const handleCalcular = async () => {
    if (!valorFinanciado || !taxaContratual || !taxaLegal || !numParcelas) {
      return;
    }

    try {
      const result = await calcularRevisional({
        valorFinanciado: parseFloat(valorFinanciado),
        taxaContratual: parseFloat(taxaContratual),
        taxaLegal: parseFloat(taxaLegal),
        numParcelas: parseInt(numParcelas)
      });
      setResultado(result);
      addToHistory({
        type: "revisionais",
        label: "Cálculo Revisional",
        values: { valorFinanciado, taxaContratual, taxaLegal, numParcelas },
        summary: `Financiado ${formatCurrency(parseFloat(valorFinanciado))} • Economia ${formatCurrency(result.economia)}`,
      });
    } catch (error) {
      console.error("Erro no cálculo:", error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cálculos Revisionais</CardTitle>
        <p className="text-sm text-muted-foreground">
          Calcule a diferença entre taxa contratual e taxa legal para empréstimos e financiamentos
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="valor">Valor Financiado</Label>
            <Input
              id="valor"
              type="number"
              placeholder="100000.00"
              value={valorFinanciado}
              onChange={(e) => setValorFinanciado(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="taxa-contratual">Taxa Contratual (% a.m.)</Label>
            <Input
              id="taxa-contratual"
              type="number"
              step="0.01"
              placeholder="2.50"
              value={taxaContratual}
              onChange={(e) => setTaxaContratual(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="taxa-legal">Taxa Legal (% a.m.)</Label>
            <Input
              id="taxa-legal"
              type="number"
              step="0.01"
              placeholder="1.80"
              value={taxaLegal}
              onChange={(e) => setTaxaLegal(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="parcelas">Número de Parcelas</Label>
            <Input
              id="parcelas"
              type="number"
              placeholder="36"
              value={numParcelas}
              onChange={(e) => setNumParcelas(e.target.value)}
            />
          </div>
        </div>
        
        <Button 
          onClick={handleCalcular} 
          className="w-full"
          disabled={loading}
        >
          {loading ? "Calculando..." : "Calcular Revisional"}
        </Button>
        
        {resultado && (
          <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
            <h3 className="font-semibold mb-2">Resultado do Cálculo:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Prestação Original:</strong> {formatCurrency(resultado.prestacaoOriginal)}</p>
                <p><strong>Prestação Corrigida:</strong> {formatCurrency(resultado.prestacaoCorrigida)}</p>
              </div>
              <div>
                <p><strong>Diferença por Parcela:</strong> {formatCurrency(resultado.diferenca)}</p>
                <p className="text-green-600"><strong>Economia Total:</strong> {formatCurrency(resultado.economia)}</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};